import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { aiService } from "@/lib/ai-service";
import {
	getCheckInAnalysisMaxCheckins,
	getCheckInAnalysisMaxMonths,
} from "@/lib/prompt-config";
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Alert,
} from "@/components/primitives";
import { Brain, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";

interface AnalysisGeneratorProps {
	onAnalysisGenerated?: () => void;
}

export function AnalysisGenerator({
	onAnalysisGenerated,
}: AnalysisGeneratorProps) {
	const { user } = useAuth();
	const [generating, setGenerating] = useState(false);
	const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
	const [message, setMessage] = useState("");
	const [details, setDetails] = useState("");

	const generateAnalysis = async () => {
		if (!user) {
			setStatus("error");
			setMessage("User not authenticated");
			return;
		}

		setGenerating(true);
		setStatus("idle");
		setMessage("");
		setDetails("");

		try {
			console.log("🔄 Manual analysis generation started for user:", user.id);

			// 1. Fetch recent check-ins (last N months per config)
			setMessage("Fetching your recent check-ins...");
			const maxMonths = getCheckInAnalysisMaxMonths();
			const cutoff = new Date();
			cutoff.setMonth(cutoff.getMonth() - maxMonths);
			const { data: checkIns, error: fetchError } = await supabase
				.from("daily_checkins")
				.select("id, general_feeling, day_description, created_at")
				.eq("user_id", user.id)
				.gte("created_at", cutoff.toISOString())
				.order("created_at", { ascending: false });

			if (fetchError) {
				throw new Error(`Failed to fetch check-ins: ${fetchError.message}`);
			}

			if (!checkIns || checkIns.length === 0) {
				setStatus("error");
				setMessage(`No check-ins found in the last ${maxMonths} months`);
				setDetails(
					"Please create some daily check-ins first before generating analysis.",
				);
				return;
			}

			// Apply limits: filter within last N months, then take up to max check-ins
			const maxCheckins = getCheckInAnalysisMaxCheckins();
			const limitedCheckIns = [...checkIns]
				.filter((ci) => new Date(ci.created_at).getTime() >= cutoff.getTime())
				.sort(
					(a, b) =>
						new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
				)
				.slice(0, Math.max(0, maxCheckins));

			console.log(
				`📋 Found ${checkIns.length} check-ins in last ${maxMonths} months; using ${limitedCheckIns.length} for analysis (max ${maxCheckins})`,
			);
			setDetails(
				`Using ${limitedCheckIns.length} check-ins from the last ${maxMonths} months (up to ${maxCheckins})`,
			);

			// 2. Generate AI analysis
			setMessage("Generating AI analysis (this may take 30-60 seconds)...");
			console.log("🤖 Calling AI analysis service...");

			const analysis = await aiService.analyzeCheckIns(limitedCheckIns);

			console.log("✅ AI analysis completed successfully");
			console.log("📊 Analysis structure:", {
				hasMainTopics: !!analysis.mainTopics,
				mainTopicsLength: analysis.mainTopics?.length,
				hasNegativeTopics: !!analysis.negativeTopics,
				negativeTopicsLength: analysis.negativeTopics?.length,
				hasPositiveTopics: !!analysis.positiveTopics,
				positiveTopicsLength: analysis.positiveTopics?.length,
				hasReflectionSuggestion: !!analysis.reflectionSuggestion,
			});

			// 3. Save analysis to database
			setMessage("Saving analysis to database...");
			const today = new Date().toISOString().split("T")[0];
			const analysisData = {
				user_id: user.id,
				analysis_date: today,
				checkin_count: limitedCheckIns.length,
				last_checkin_date:
					limitedCheckIns.length > 0
						? limitedCheckIns[0].created_at.split("T")[0]
						: today,
				analysis_data: {
					mainTopics: analysis.mainTopics || [],
					negativeTopics: analysis.negativeTopics || [],
					positiveTopics: analysis.positiveTopics || [],
					reflectionSuggestion: analysis.reflectionSuggestion || null,
				},
			};

			console.log("💾 Saving analysis to database...");

			// Check if analysis already exists for today
			const { data: existingAnalysis, error: existingError } = await supabase
				.from("daily_checkin_analysis")
				.select("id")
				.eq("user_id", user.id)
				.eq("analysis_date", today)
				.maybeSingle();

			if (existingError) {
				throw new Error(
					`Failed to check existing analysis: ${existingError.message}`,
				);
			}

			if (existingAnalysis) {
				// Update existing analysis
				console.log("📝 Updating existing analysis record...");
				const { error: updateError } = await supabase
					.from("daily_checkin_analysis")
					.update({
						...analysisData,
						updated_at: new Date().toISOString(),
					})
					.eq("id", existingAnalysis.id);

				if (updateError) {
					throw new Error(`Failed to update analysis: ${updateError.message}`);
				}

				console.log("✅ Analysis updated successfully");
			} else {
				// Insert new analysis
				console.log("📝 Creating new analysis record...");
				const { error: insertError } = await supabase
					.from("daily_checkin_analysis")
					.insert({
						...analysisData,
						created_at: new Date().toISOString(),
						updated_at: new Date().toISOString(),
					});

				if (insertError) {
					throw new Error(`Failed to insert analysis: ${insertError.message}`);
				}

				console.log("✅ Analysis created successfully");
			}

			// 4. Verify the analysis was saved
			const { data: verifyAnalysis, error: verifyError } = await supabase
				.from("daily_checkin_analysis")
				.select("analysis_date, checkin_count, analysis_data")
				.eq("user_id", user.id)
				.eq("analysis_date", today)
				.single();

			if (verifyError) {
				throw new Error(
					`Failed to verify saved analysis: ${verifyError.message}`,
				);
			}

			console.log("✅ Analysis verification successful");
			setStatus("success");
			setMessage("Analysis generated and saved successfully!");
			setDetails(
				`Analyzed ${verifyAnalysis.checkin_count} check-ins and saved insights for ${verifyAnalysis.analysis_date}`,
			);

			// Notify parent component
			if (onAnalysisGenerated) {
				onAnalysisGenerated();
			}
		} catch (error) {
			console.error("❌ Error generating analysis:", error);
			setStatus("error");
			setMessage("Failed to generate analysis");
			setDetails(
				error instanceof Error ? error.message : "An unexpected error occurred",
			);
		} finally {
			setGenerating(false);
		}
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center space-x-2">
					<Brain className="h-5 w-5" />
					<span>Generate Analysis</span>
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="text-sm text-muted-foreground">
					Generate fresh AI-powered insights from your recent check-ins.
				</div>

				{status === "success" && (
					<Alert>
						<CheckCircle className="h-4 w-4" />
						<div>
							<div className="font-medium">{message}</div>
							<div className="text-sm mt-1">{details}</div>
						</div>
					</Alert>
				)}

				{status === "error" && (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<div>
							<div className="font-medium">{message}</div>
							<div className="text-sm mt-1">{details}</div>
						</div>
					</Alert>
				)}

				{generating && (
					<div className="space-y-2">
						<div className="flex items-center space-x-2">
							<RefreshCw className="h-4 w-4 animate-spin" />
							<span className="text-sm">
								{message || "Generating analysis..."}
							</span>
						</div>
						{details && (
							<div className="text-xs text-muted-foreground">{details}</div>
						)}
					</div>
				)}

				<Button
					onClick={generateAnalysis}
					disabled={generating}
					className="w-full"
				>
					{generating ? (
						<>
							<RefreshCw className="h-4 w-4 mr-2 animate-spin" />
							Generating...
						</>
					) : (
						<>
							<Brain className="h-4 w-4 mr-2" />
							Generate Fresh Analysis
						</>
					)}
				</Button>

				<div className="text-xs text-muted-foreground">
					This will analyze your last 28 days of check-ins and create/update
					today's analysis.
				</div>
			</CardContent>
		</Card>
	);
}
